import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AdditionalInput } from '@/utils/order-submitted';
import { Button } from './button';
import { createStyles } from '@/contexts/theme-context';
import { InvoiceDetailList } from './payments/invoice-detail-list';
import {
  LIST_ITEM_SEPARATOR_HEIGHT,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { ListEmptyState } from './list-empty-state';
import { OrderDetailList, OrderItemDataType } from './orders/order-detail-list';
import { OrderFee } from '@/utils/money';
import {
  OrderInvoiceItemsFieldsFragment,
  OrderInvoiceLineItemFieldsFragment,
  Orders_GetOrderFieldsFragment,
} from '@/types';
import { OrderSummaryInputID } from '@/enums';
import { ScrollViewProps, Text, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';

export interface Props {
  displayItemPrices: boolean;
  filteredData?: (OrderInvoiceLineItemFieldsFragment | OrderItemDataType)[];
  isReviewable?: boolean | null;
  isV2Invoice: boolean;
  onPressReviewButton?: () => void;
  order: Orders_GetOrderFieldsFragment;
  orderFees: OrderFee[];
  orderInvoice?: OrderInvoiceItemsFieldsFragment | null;
  scrollViewProps?: ScrollViewProps;
  searchTerm?: string;
}

export const Invoice: React.FC<Props> = ({
  displayItemPrices,
  filteredData,
  isReviewable,
  isV2Invoice,
  onPressReviewButton,
  order,
  orderInvoice,
  orderFees,
  scrollViewProps,
  searchTerm = '',
}) => {
  const { isSmallLayout, isLargeLayout } = useLayoutContext();
  const [styles] = useStyles();

  const useInvoicePrices = Boolean(orderInvoice) && !isV2Invoice;
  const supplierMetadata = order?.supplier.supplier_metadata;
  const displayItemCode = supplierMetadata?.display_item_code ?? false;
  const gtinSettings = supplierMetadata?.gtin_settings;

  const additionalInputs = useMemo(() => {
    const inputs: AdditionalInput[] = [];

    if (order?.po_number) {
      inputs.push({
        id: OrderSummaryInputID.PONumber,
        title: i18n.t('po_number'),
        value: order.po_number,
      });
    }

    if (order?.notes) {
      inputs.push({
        id: OrderSummaryInputID.Notes,
        title: i18n.t('order_notes'),
        value: order.notes,
      });
    }

    if (Array.isArray(order?.additional_input_values)) {
      // Exclude any preset input IDs
      const filteredInputIDSet = new Set(Object.values(OrderSummaryInputID));
      for (const aiv of order.additional_input_values) {
        if (!filteredInputIDSet.has(aiv.id)) {
          inputs.push(aiv);
        }
      }
    }

    return inputs;
  }, [order]);

  const renderReviewButtonInFooter =
    isReviewable && (isLargeLayout || !orderInvoice);
  const renderFooter = Boolean(
    additionalInputs.length || renderReviewButtonInFooter,
  );

  const noSearchResults =
    searchTerm.length > 0 ? (
      <ListEmptyState
        text={i18n.t('no_results_for', {
          searchTerm,
        })}
      />
    ) : null;

  // If the order has an invoice, and the distributor is on Invoice V2,
  // we display the invoice line items so they match the displayed total
  return orderInvoice && isV2Invoice ? (
    <InvoiceDetailList
      displayItemPrices={displayItemPrices}
      invoiceLineItems={
        filteredData
          ? (filteredData as OrderInvoiceLineItemFieldsFragment[])
          : orderInvoice.order_invoice_line_items
      }
      ListEmptyComponent={noSearchResults}
      scrollViewProps={scrollViewProps}
    />
  ) : (
    <OrderDetailList
      displayItemCode={displayItemCode}
      displayItemPrices={displayItemPrices}
      displayPlaceholder={displayItemPrices}
      gtinSettings={gtinSettings}
      hideMissingItems={Boolean(orderInvoice)}
      ListEmptyComponent={noSearchResults}
      ListFooterComponent={
        renderFooter ? (
          <View
            style={[
              styles.listFooter,
              isSmallLayout ? styles.listFooterSmall : styles.listFooterLarge,
            ]}
          >
            {additionalInputs.map(({ id, title, value }) => (
              <View key={id} style={styles.orderTextFieldContainer}>
                <Text
                  style={[TYPOGRAPHY.pSemiBold, styles.orderTextFieldHeader]}
                >
                  {title}
                </Text>
                <Text style={TYPOGRAPHY.p}>{value}</Text>
              </View>
            ))}
            {renderReviewButtonInFooter && onPressReviewButton ? (
              <Button
                onPress={onPressReviewButton}
                style={styles.somethingWrongButton}
                textStyle={styles.somethingWrongText}
                trackingData={{
                  name: 'order_detail.report_button',
                  properties: {
                    orderUUID: order.uuid,
                  },
                }}
                type="tertiary"
              >
                {i18n.t('something_wrong')}
              </Button>
            ) : null}
          </View>
        ) : null
      }
      orderFees={searchTerm.length === 0 ? orderFees : []}
      orderItems={
        filteredData
          ? (filteredData as OrderItemDataType[])
          : (order.orders_items ?? [])
      }
      scrollViewProps={scrollViewProps}
      useInvoicePrices={useInvoicePrices}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listFooter: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: LIST_ITEM_SEPARATOR_HEIGHT,
    marginHorizontal: SCREEN_PADDING,
    marginTop: 16,
    paddingHorizontal: 16,
  },
  listFooterLarge: {
    marginHorizontal: 16,
  },
  listFooterSmall: {
    marginHorizontal: 8,
  },
  orderTextFieldContainer: {
    paddingTop: 16,
  },
  orderTextFieldHeader: {
    marginBottom: 4,
  },
  somethingWrongButton: {
    height: 'auto',
    marginTop: 32,
  },
  somethingWrongText: {
    textAlign: 'center',
  },
}));
